/**
 * Runtime stream-health registry.
 *
 * Health data is produced by `public/check-stream-health.php` (cPanel cron) and
 * stored in Firebase RTDB at `/channels_health`. The data is loaded once at
 * app startup by `ChannelDataContext` and pushed in here via `setHealthMap`.
 *
 * Until health data has been hydrated, every URL is treated as "unknown" —
 * which means no channels are hidden and the player still tries them in their
 * original order. This is the safe default.
 *
 * Shape of the map:
 *   { [url]: { ok: boolean, status?: number, error?: string, ms?: number, checkedAt?: number } }
 */

let healthMap = Object.freeze({});
let generatedAt = null;
const subscribers = new Set();

export function setHealthMap(map, when) {
  healthMap = map && typeof map === 'object' ? map : {};
  generatedAt = typeof when === 'number' ? when : null;
  subscribers.forEach((cb) => {
    try { cb(); } catch (_) { /* noop */ }
  });
}

export function subscribeHealth(cb) {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

export function getHealthGeneratedAt() {
  return generatedAt;
}

/** True if we have a recorded `ok: true` for this URL. */
export function isUrlKnownOk(url) {
  if (!url) return false;
  const entry = healthMap[url];
  return Boolean(entry && entry.ok === true);
}

/** True if we have a recorded `ok: false` for this URL. Unknown URLs return false. */
export function isUrlKnownDead(url) {
  if (!url) return false;
  const entry = healthMap[url];
  return Boolean(entry && entry.ok === false);
}

/**
 * Reorder `iptv_urls` so known-good URLs come first, unknown URLs next,
 * known-dead URLs last (kept as a last-resort attempt rather than dropped).
 * Pure — returns a new array; never mutates input.
 */
export function reorderUrlsByHealth(urls) {
  if (!Array.isArray(urls) || urls.length <= 1) return Array.isArray(urls) ? urls.slice() : [];
  const ok = [];
  const unknown = [];
  const dead = [];
  for (const u of urls) {
    if (!u) continue;
    if (isUrlKnownOk(u)) ok.push(u);
    else if (isUrlKnownDead(u)) dead.push(u);
    else unknown.push(u);
  }
  return [...ok, ...unknown, ...dead];
}

/**
 * A channel is considered playable when at least one of:
 *  - it has any `youtube_urls` entry (always treated as playable, we don't probe YouTube), or
 *  - at least one `iptv_urls` entry is not known-dead (i.e. ok or unchecked).
 *
 * Channels with NO youtube AND every iptv URL marked dead → hide.
 */
export function isChannelLikelyPlayable(channel) {
  if (!channel || typeof channel !== 'object') return false;
  const youtube = Array.isArray(channel.youtube_urls) ? channel.youtube_urls.filter(Boolean) : [];
  if (youtube.length > 0) return true;
  const iptv = Array.isArray(channel.iptv_urls) ? channel.iptv_urls.filter(Boolean) : [];
  if (iptv.length === 0) return false;
  return iptv.some((u) => !isUrlKnownDead(u));
}

/**
 * Returns a new channel object with `iptv_urls` reordered by health.
 * Preserves original reference if no reordering is needed.
 */
export function withHealthSortedUrls(channel) {
  if (!channel || typeof channel !== 'object') return channel;
  const iptv = Array.isArray(channel.iptv_urls) ? channel.iptv_urls : [];
  if (iptv.length <= 1) return channel;
  const reordered = reorderUrlsByHealth(iptv);
  let changed = reordered.length !== iptv.length;
  if (!changed) {
    for (let i = 0; i < reordered.length; i += 1) {
      if (reordered[i] !== iptv[i]) {
        changed = true;
        break;
      }
    }
  }
  if (!changed) return channel;
  return { ...channel, iptv_urls: reordered };
}

/** Apply both filtering and reordering in a single pass. */
export function applyHealthToChannelList(channels) {
  if (!Array.isArray(channels)) return [];
  const out = [];
  for (const ch of channels) {
    if (!isChannelLikelyPlayable(ch)) continue;
    out.push(withHealthSortedUrls(ch));
  }
  return out;
}

export function applyHealthToChannelMap(map) {
  if (!map || typeof map !== 'object') return {};
  const out = {};
  for (const [k, v] of Object.entries(map)) {
    out[k] = applyHealthToChannelList(v);
  }
  return out;
}
